"use client";

import React, { useState, useEffect } from "react";
import { Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";

function ProductGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    setPage,
    isRefetching,
  } = useEcommerceProducts();

  
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 3; i++) pages.push(i);
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 2; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("ellipsis");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="flex flex-col space-y-4 rounded-xl border border-border/40 p-4"
          >
            <div className="aspect-square w-full animate-pulse rounded-lg bg-muted/50" />
            <div className="h-6 w-3/4 animate-pulse rounded bg-muted/50" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-muted/50" />
            <div className="mt-auto h-10 w-full animate-pulse rounded bg-muted/50" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center text-center">
        <p className="mb-4 text-lg text-muted-foreground">
          Si è verificato un errore nel caricamento dei prodotti.
        </p>
        <button
          onClick={() => refetch()}
          className="text-primary underline hover:text-primary/80"
        >
          Riprova
        </button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center text-center">
        <p className="text-lg text-muted-foreground">
          Nessun prodotto trovato in questa categoria.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-16">
      {}
      <div className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => (
          <div
            key={product.id}
            data-index={i}
            className="group flex flex-col overflow-hidden rounded-xl bg-background transition-all duration-300 hover:shadow-lg"
          >
            <Link
              to={product.link}
              className="relative aspect-square overflow-hidden rounded-xl bg-muted/20"
            >
              {product.featured_image_url ? (
                <img
                  src={product.featured_image_url}
                  alt={product.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-muted/20 text-muted-foreground">
                  <span className="text-sm">Nessuna immagine</span>
                </div>
              )}
              {product.discounted_price && product.price > product.discounted_price && (
                <span className="absolute top-3 left-3 rounded-full bg-destructive px-3 py-1 text-xs font-medium text-white shadow-sm">
                  Offerta
                </span>
              )}
            </Link>

            <div className="flex flex-1 flex-col pt-6">
              <Link to={product.link} className="group-hover:text-primary">
                <h3 className="line-clamp-2 text-lg font-semibold tracking-tight text-foreground transition-colors">
                  {product.title}
                </h3>
              </Link>

              <div className="mt-2 mb-6 flex items-baseline gap-2">
                {product.discounted_price ? (
                  <>
                    <span className="text-xl font-bold text-primary">
                      €
                      {product.discounted_price.toLocaleString("it-IT", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                    <span className="text-sm text-muted-foreground line-through">
                      €
                      {product.price.toLocaleString("it-IT", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </>
                ) : (
                  <span className="text-xl font-bold text-primary">
                    {product.price !== undefined
                      ? `€${product.price.toLocaleString("it-IT", {
                          minimumFractionDigits: 2,
                        })}`
                      : ""}
                  </span>
                )}
              </div>

              <div className="mt-auto">
                <AddToCartButton
                  productId={product.id}
                  variant="default"
                  className="w-full"
                  buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-lg font-medium transition-all shadow-sm"
                  text="Aggiungi al carrello"
                  showQuantity={false}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {}
      {totalPages > 1 && (
        <div className="mt-16 border-t border-border/40 pt-8">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (hasPrev) prevPage();
                  }}
                  className={
                    !hasPrev
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer hover:bg-muted hover:text-foreground"
                  }
                />
              </PaginationItem>

              {getPageNumbers().map((page, idx) => (
                <PaginationItem key={idx} data-index={idx}>
                  {page === "ellipsis" ? (
                    <PaginationEllipsis />
                  ) : (
                    <PaginationLink
                      href="#"
                      isActive={currentPage === page}
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(page as number);
                      }}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  )}
                </PaginationItem>
              ))}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (hasNext) nextPage();
                  }}
                  className={
                    !hasNext
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer hover:bg-muted hover:text-foreground"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
          {isRefetching && (
            <div className="mt-4 flex justify-center">
              <Loader className="h-5 w-5 animate-spin text-primary" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProductGridAndPagination() {
  
  const [wpQuery, setWpQuery] = useState<WP_Query | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.wvcClient) {
      const currentParams = window.wvcClient.getCurrentWPQueryParams?.() || {};
      
      if (currentParams && typeof currentParams === "object") {
        const query = new WP_Query({
          ...currentParams,
          post_type: "product",
          posts_per_page: 9,
        });
        setWpQuery(query);
      }
    }
  }, []);

  if (!wpQuery) {
    return (
      <section id="product-grid-and-pagination" className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                data-index={i}
                className="h-96 animate-pulse rounded-xl bg-muted/30"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="product-grid-and-pagination"
      className="bg-background py-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <EcommerceProductsProvider wp_query={wpQuery} className="w-full" postType="product">
          <ProductGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}